import { useEffect, useMemo, useState, useCallback } from 'react'
import { loader } from '../../lib/loader'
import { useSnackbar } from '../../components/SnackbarContext'

import {
	CreditScoreOutlined as CreditScoreOutlinedIcon,
	ReceiptLongOutlined as ReceiptLongOutlinedIcon,
} from '@mui/icons-material'

import PaymentHistoryTable from '../../components/settings/payments/PaymentHistoryTable'
import PaymentSummaryCard from '../../components/settings/payments/PaymentSummaryCard'
import PlansModal from "../../components/modal/PlansModal"

import { getPaymentsPageConfig } from '../../services/payments-config'
import { getScoutEnterprise } from '../../services/scout'
import { getPaymentHistory } from '../../services/payments'
import { isTrial } from "../../services/utils"
import { formatBRL, getRenewalDate } from "../../utils"

const formatDate = (value) => {
	if (!value) return ""

	let parsedDate

	if (value instanceof Date) {
		parsedDate = value
	} else if (typeof value === "string") {
		parsedDate = value.includes("T")
			? new Date(value)
			: new Date(value + "T00:00:00")
	} else {
		return ""
	}

	if (Number.isNaN(parsedDate.getTime())) return ""

	return parsedDate.toLocaleDateString("pt-BR")
}

const roundCurrency = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100

const resolveStatusLabel = (status) => {
	switch (String(status).toUpperCase()) {
		case 'PAID':
			return 'Pago'
		case 'PENDING':
			return 'Em aberto'
		case 'CANCELLED':
			return 'Cancelado'
		default:
			return status
	}
}

const resolveStatusTone = (status) => {
	switch (String(status).toUpperCase()) {
		case 'PAID':
			return 'paid'
		case 'PENDING':
			return 'open'
		case 'CANCELLED':
			return 'late'
		default:
			return 'open'
	}
}

const resolveMoney = (value) => {
	if (value === null || value === undefined || value === '') {
		return '—'
	}

	const numericValue = Number(value)

	if (Number.isNaN(numericValue)) {
		return String(value)
	}

	return formatBRL(roundCurrency(numericValue))
}

const getSortedHistory = (items = []) => {
	return [...items].sort((first, second) => {
		const firstDate = new Date(first?.due_at || first?.created_at || 0).getTime()
		const secondDate = new Date(second?.due_at || second?.created_at || 0).getTime()

		return secondDate - firstDate
	})
}

const Payments = () => {
	const { showSnackbar } = useSnackbar()
	const [enterprise, setEnterprise] = useState(null)
	const [history, setHistory] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const paymentsConfig = useMemo(() => getPaymentsPageConfig(), [])
	const [showPlans, setShowPlans] = useState(false)

	useEffect(() => {
		let isMounted = true

		const load = async () => {
			setLoading(true)
			loader.on()
			setError('')

			try {
				const [enterpriseResult, historyResult] = await Promise.allSettled([getScoutEnterprise(), getPaymentHistory()])
				if (!isMounted) return

				const resolvedEnterprise = enterpriseResult.status === 'fulfilled' ? enterpriseResult.value : null
				const enterpriseHistory = Array.isArray(resolvedEnterprise?.history) ? resolvedEnterprise.history : []
				const resolvedHistory = enterpriseHistory.length
					? enterpriseHistory
					: (historyResult.status === 'fulfilled' && Array.isArray(historyResult.value) ? historyResult.value : [])

				setEnterprise(resolvedEnterprise)
				setHistory(resolvedHistory)

				if (enterpriseResult.status === 'rejected') {
					const errorMessage = enterpriseResult.reason?.message || 'Erro ao carregar dados de pagamento'
					setError(errorMessage)
					showSnackbar({ message: errorMessage, severity: 'error' })
				} else if (!enterpriseHistory.length && historyResult.status === 'rejected') {
					const errorMessage = historyResult.reason?.message || 'Erro ao carregar histórico de pagamentos'
					setError(errorMessage)
					showSnackbar({ message: errorMessage, severity: 'error' })
				}
			} finally {
				if (isMounted) setLoading(false)
				loader.off()
			}
		}

		load()

		return () => {
			isMounted = false
		}
	}, [showSnackbar])

	const getPlanValue = useCallback((enterprise, history) => {
		if (enterprise?.external_monthly_price)
			return resolveMoney(enterprise?.external_monthly_price)

		if (history.length > 0)
			return resolveMoney(history[0].payed_value)

		const valueFromProducts = enterprise?.plan?.products?.reduce((total, product) => {
			return total + (product.monthly_value)
		}, 0) || 0
		if (valueFromProducts)
			return resolveMoney(valueFromProducts)

		return resolveMoney(0)
	}, [])

	const getTrialPeriodInfo = useCallback((enterprise) => {
		const endDateStr = enterprise?.end_charging
		if (!endDateStr)
			return "Período de teste não definido."

		const today = new Date()
		const endDate = new Date(`${endDateStr}T00:00:00`)
		today.setHours(0, 0, 0, 0)

		if (endDate >= today)
			return `Período encerra em ${formatDate(endDateStr)}.`

		return `Período encerrado em ${formatDate(endDateStr)}.`
	}, [])

	const subscription = useMemo(() => {
		const renewalDate = getRenewalDate(enterprise)
		const renewalLabel = (isTrial(enterprise))
			? getTrialPeriodInfo(enterprise)
			: (enterprise?.charge_status === "COMPLIANT" && renewalDate)
				? `Será renovado em ${formatDate(renewalDate)}.`
				: (enterprise?.charge_status === "DEFAULTER30")
					? `Última cobrança ${formatDate(renewalDate)}.`
					: ""

		const planValue = getPlanValue(enterprise, history)
		const planName = enterprise?.plan?.name ? `Plano ${enterprise.plan.name}` : paymentsConfig?.subscription?.name || 'Plano'
		const planMonthly = Array.isArray(enterprise?.plan?.products)
			? enterprise.plan.products.reduce((sum, item) => sum + (item.monthly_value || 0), 0)
			: null
		const planPrice = planValue !== '—'
			? `${planValue}/mês`
			: planMonthly != null
				? `${formatBRL(roundCurrency(planMonthly / 0.95))}/mês`
				: (paymentsConfig?.subscription?.price || '—')

		let details = ""
		if (enterprise?.charge_status !== "COMPLIANT" && renewalDate) {
			switch (enterprise?.charge_status) {
				case "DEFAULTER30":
					details = `Com pendência no pagamento.`
					break
				case "SCHEDULED_CANCEL":
					details = `Cancelamento agendado para ${formatDate(renewalDate)}.`
					break
				case "CANCELLED":
					details = `Cancelado em ${formatDate(enterprise?.end_charging)}.`
					break
				default:
					break
			}
		}

		return {
			name: planName,
			details: details,
			price: planPrice,
			renewalLabel,
			primaryAction: enterprise
				? {
					label: "Alterar assinatura",
					mobileLabel: "Alterar assinatura",
					type: "change-plan"
				}
				: paymentsConfig?.subscription?.primaryAction,
		}
	}, [enterprise, history, paymentsConfig, getPlanValue, getTrialPeriodInfo])

	const handleAction = (action) => {
		if (!action?.type) {
			return
		}

		if (action.type === "change-plan") {
			setShowPlans(true)
			return
		}

		if (action.type === 'mailto' && action.href) {
			window.location.href = action.href
			return
		}

		if (action.type === 'comingSoon') {
			return
		}

		if (action.type === 'openInvoice' && action.url) {
			window.open(action.url, '_blank')
			return
		}
	}

	const mappedInvoices = useMemo(() => {
		if (!history.length) {
			return []
		}

		const companyHistory = history.filter((item) => {
			if (!enterprise?.id || item?.enterprise == null) {
				return true
			}

			return Number(item.enterprise) === Number(enterprise.id)
		})

		return getSortedHistory(companyHistory).map((invoice) => {
			const chargeDateSource = invoice.payed_at
			const totalValue = resolveMoney(invoice.payed_value)
			const status = resolveStatusTone(invoice.status)
			const actionUrl = invoice.invoice

			return {
				id: invoice.id,
				chargeDate: formatDate(chargeDateSource) || '—',
				total: totalValue || '—',
				dueDate: formatDate(chargeDateSource) || '—',
				status,
				statusLabel: resolveStatusLabel(invoice.status),
				actionLabel: 'Baixar comprovante',
				action: actionUrl ? { type: 'openInvoice', url: actionUrl } : null,
			}
		})
	}, [enterprise, history])

	return (
		<>
			<section className="scout-settings-section">
				<div className="scout-settings-section__label">
					<CreditScoreOutlinedIcon />
					<span>Sua assinatura Kwik Scout</span>
				</div>
				<PaymentSummaryCard subscription={subscription} onAction={handleAction} />
			</section>

			<section className="scout-settings-section">
				<div className="scout-settings-section__label">
					<ReceiptLongOutlinedIcon />
					<span>Faturas e histórico de pagamentos</span>
				</div>
				<PaymentHistoryTable invoices={mappedInvoices} onAction={handleAction} loading={loading} error={error} />
			</section>

			{showPlans &&
				<PlansModal
					handleClose={() => setShowPlans(false)}
					enterprise={enterprise}
					setEnterprise={setEnterprise}
				/>
			}
		</>
	)
}

export default Payments
